import React from 'react';
import {
  SimpleGrid,
  Box,
  Flex,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider
} from '@chakra-ui/react';

// Format numbers in a compact way (e.g., 1000 -> 1k)
const formatCompact = (value) => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1) + 'k';
  } else if (value >= 1) {
    return value.toFixed(1);
  } else {
    // For small decimal values
    return value.toFixed(3);
  }
};

// Get rank based on SOL contributed
const getRank = (solAmount) => {
  if (solAmount >= 50) return { rank: "Blue Whale", emoji: "🐳", color: "#ff1f69" };
  if (solAmount >= 30) return { rank: "Whale", emoji: "🐋", color: "#ff9f1f" };
  if (solAmount >= 20) return { rank: "Shark", emoji: "🦈", color: "#ffe248" };
  if (solAmount >= 10) return { rank: "Dolphin", emoji: "🐬", color: "#7ed957" };
  if (solAmount >= 5) return { rank: "Fish", emoji: "🐟", color: "#38b6ff" };
  if (solAmount >= 3) return { rank: "Tropical Fish", emoji: "🐠", color: "#fd2626" };
  if (solAmount >= 2) return { rank: "Blowfish", emoji: "🐡", color: "#ff914d" };
  if (solAmount >= 1) return { rank: "Octopus", emoji: "🐙", color: "#ffde59" };
  if (solAmount >= 0.5) return { rank: "Lobster", emoji: "🦞", color: "#2fdb88" };
  if (solAmount >= 0.1) return { rank: "Shrimp", emoji: "🦐", color: "#159fef" };
  return { rank: "No Rank", emoji: "🔍", color: "#888888" };
};

// Calculate percentage change
const calculateChange = (current, previous) => {
  if (previous === 0) return 100;
  return ((current - previous) / previous) * 100;
};

const DashboardValueBoxes = ({ chartData, timeframe, userStats }) => {
  // Get first and last data points based on timeframe
  const data = chartData || [];
  const firstPoint = data.length > 0 ? data[0] : {};
  const lastPoint = data.length > 0 ? data[data.length - 1] : {};
  
  // Use userStats if available, otherwise fallback to chart data
  const solContributed = userStats?.solContributed || (lastPoint?.userSolContributed || 2.5);
  const totalReferrals = userStats?.totalReferrals || (lastPoint?.totalReferrals || 8);
  const totalEarnings = userStats?.totalEarnings || (lastPoint?.totalEarnings || 250);
  const daoPower = userStats?.daoPower || (lastPoint?.daoPower || 185);

  // Define the metrics to display
  const metrics = [
    { 
      name: 'Rank', 
      startValue: firstPoint?.userSolContributed || 0.5, 
      endValue: solContributed,
      isRank: true,
      color: '#00E5FF',
      bgGradient: 'linear(to-r, rgba(0, 229, 255, 0.1), rgba(0, 229, 255, 0.02))'
    },
    { 
      name: 'Affiliates', 
      startValue: firstPoint?.totalReferrals || 3, 
      endValue: totalReferrals,
      secondaryValue: totalEarnings,
      secondaryLabel: 'Earned',
      color: '#00FF85',
      bgGradient: 'linear(to-r, rgba(0, 255, 133, 0.1), rgba(0, 255, 133, 0.02))'
    },
    { 
      name: 'DAO Power', 
      startValue: firstPoint?.daoPower || 120, 
      endValue: daoPower,
      color: '#FF00E5',
      bgGradient: 'linear(to-r, rgba(255, 0, 229, 0.1), rgba(255, 0, 229, 0.02))'
    },
    { 
      name: 'Jackpot', 
      startValue: firstPoint.progressiveJackpot || 10000, 
      endValue: lastPoint.progressiveJackpot || 15000,
      color: '#E5FF00',
      bgGradient: 'linear(to-r, rgba(229, 255, 0, 0.1), rgba(229, 255, 0, 0.02))'
    },
    { 
      name: 'Players', 
      startValue: firstPoint.activePlayers || 1500, 
      endValue: lastPoint.activePlayers || 1950,
      color: '#A78BFA',
      bgGradient: 'linear(to-r, rgba(167, 139, 250, 0.1), rgba(167, 139, 250, 0.02))'
    },
    { 
      name: 'Borrowers', 
      startValue: firstPoint.activeBorrowers || 250, 
      endValue: lastPoint.activeBorrowers || 320,
      color: '#FFA500',
      bgGradient: 'linear(to-r, rgba(255, 165, 0, 0.1), rgba(255, 165, 0, 0.02))'
    },
    { 
      name: 'Stakers', 
      startValue: firstPoint.activeStakers || 600, 
      endValue: lastPoint.activeStakers || 780,
      color: '#00D1FF',
      bgGradient: 'linear(to-r, rgba(0, 209, 255, 0.1), rgba(0, 209, 255, 0.02))'
    },
    { 
      name: 'Lenders', 
      startValue: firstPoint.activeLenders || 350, 
      endValue: lastPoint.activeLenders || 450,
      color: '#FF5733',
      bgGradient: 'linear(to-r, rgba(255, 87, 51, 0.1), rgba(255, 87, 51, 0.02))'
    }
  ];

  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4} mb={4}>
      {metrics.map((metric, index) => {
        const percentChange = calculateChange(metric.endValue, metric.startValue);
        const isPositive = percentChange >= 0;
        
        return (
          <Box
            key={index}
            p={3}
            bg="black"
            borderRadius="md"
            borderWidth="1px"
            borderColor={`${metric.color}40`}
            boxShadow={`0 0 10px ${metric.color}20`}
            bgGradient={metric.bgGradient}
            _hover={{ 
              boxShadow: `0 0 15px ${metric.color}30`,
              borderColor: `${metric.color}60` 
            }}
            transition="all 0.3s"
          >
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">{metric.name}</StatLabel>
              <StatNumber color={metric.color} fontSize="lg" fontWeight="bold">
                {metric.isRank 
                  ? getRank(metric.endValue).emoji + ' ' + getRank(metric.endValue).rank
                  : formatCompact(metric.endValue)}
              </StatNumber>
              <Flex justify="space-between" align="center">
                {metric.secondaryValue ? (
                  <Text fontSize="xs" color="green.400" fontWeight="bold">
                    {metric.secondaryLabel}: {formatCompact(metric.secondaryValue)} JINU
                  </Text>
                ) : (
                  <StatHelpText mb={0} fontSize="xs">
                    <StatArrow type={isPositive ? 'increase' : 'decrease'} />
                    {Math.abs(percentChange).toFixed(1)}%
                  </StatHelpText>
                )}
                <Text fontSize="xs" color="gray.500">
                  {metric.isRank 
                    ? 'SOL: ' + metric.endValue
                    : 'From ' + formatCompact(metric.startValue)}
                </Text>
              </Flex>
            </Stat>
          </Box>
        );
      })}
    </SimpleGrid>
  );
};

export default DashboardValueBoxes;
